import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { useFormik } from 'formik';

import { colors, distances, grid } from '../../../styles/constants';
import { Input } from '../../../components/Forms';
import { Button, ButtonGroup } from '../../../components/Buttons';
import { validate, validateRequired } from '../../../helpers/validation';
import { forgottenPassword } from '../../cognitoIdentityWrapper';
import { stringToSlug } from '../helpers';

interface ForgotPasswordProps {
    username: string;
    setSubmitUsername: (username: string) => void;
    setBack: (username: string) => void;
    setShake: (value: boolean) => void;
}

const ForgotPassword = ({
    username,
    setBack,
    setSubmitUsername,
    setShake,
}: ForgotPasswordProps) => {
    const { t } = useTranslation();
    const [loginError, setLoginError] = useState('');
    const formik = useFormik({
        initialValues: {
            username: username,
        },
        validate: (values) => {
            let errors: { [key: string]: string } = {};
            const validators = [
                {
                    path: 'username',
                    validator: validateRequired(t('login.invalid.username')),
                },
            ];
            errors = validators.reduce((acc, elem) => {
                return validate(elem.path, elem.validator, values, acc);
            }, errors);

            return errors;
        },
        onSubmit: async (values) => {
            try {
                await forgottenPassword(values.username.toLowerCase());
                setSubmitUsername(values.username);
            } catch (error) {
                console.log('Error forgot password ', error);
                setLoginError(error.message);
                setShake(true);
            }
        },
    });
    return (
        <>
            <p>{t('login.forgot_password_description')}</p>
            <ErrorMessage visible={loginError !== ''}>
                {t('login.forgot_password_error', { context: stringToSlug(loginError) })}
            </ErrorMessage>
            <form onSubmit={formik.handleSubmit}>
                <InputWrapper>
                    <Input
                        label={t('login.fields.username')}
                        placeholder={t(`login.placeholders.username`)}
                        type="email"
                        name="username"
                        autoComplete="username"
                        value={formik.values.username}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        autoFocus={true}
                        disabled={formik.isSubmitting}
                    />
                </InputWrapper>
                <StyledButtonGroup>
                    <Button type="submit" disabled={!formik.isValid || formik.isSubmitting}>
                        {t('login.actions.forgotten_password')}
                    </Button>
                    <LinkLike
                        onClick={() => {
                            setBack(formik.values.username);
                        }}
                    >
                        {t('login.actions.back')}
                    </LinkLike>
                </StyledButtonGroup>
            </form>
        </>
    );
};

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;

const LinkLike = styled.span`
    color: ${colors.primary};
    user-select: none;
    cursor: pointer;

    &:hover {
        color: ${colors.primaryHover};
    }
`;

const StyledButtonGroup = styled(ButtonGroup)`
    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        margin-right: ${distances.tiny};
    }
`;

interface ErrorMessageProps {
    visible: boolean;
}

const ErrorMessage = styled.p<ErrorMessageProps>`
    font-size: 11px;
    line-height: ${distances.small};
    color: ${colors.invalid};
    visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

export default ForgotPassword;
